import React, { useState, useEffect } from 'react';
import { Box, Button, Divider, List, ListItem, ListItemIcon, ListItemText, Typography, Badge, Pagination } from '@mui/material';
import { DescriptionOutlined, NotificationsActive } from '@mui/icons-material';
import { ModalGeneral } from 'ui-component';
import moment from 'moment';
import 'moment/locale/es';

const ITEMS_POR_PAGINA = 3;

const fmt = (f) => (!f ? '' : moment(f).format('DD/MM/YYYY'));
const horaFin = () => '11:59 p.m.';

const getBadgeInfo = (fechaInicio, fechaFin, diasDuracion) => {
    if (!fechaFin) return null;

    const hoy = moment().startOf('day');
    const fin = moment(fechaFin).startOf('day');
    const diasRestantes = fin.diff(hoy, 'days');
    const fmtInicio = fechaInicio ? fmt(fechaInicio) : null;
    const duracion = diasDuracion ? `${diasDuracion} ${diasDuracion === 1 ? 'día' : 'días'} ` : '';

    let label = '';
    let color = { bg: '#E8F5E9', text: '#1B5E20' };

    if (diasRestantes === 0) {
        label = fmtInicio
            ? `Disponible ${duracion}del ${fmtInicio} hasta hoy ${fmt(fechaFin)} ${horaFin()}`
            : `Disponible ${duracion}hasta hoy ${fmt(fechaFin)} ${horaFin()}`;
        color = { bg: '#FFEBEE', text: '#B71C1C' };
    } else if (diasRestantes === 1) {
        label = fmtInicio
            ? `Disponible ${duracion}del ${fmtInicio} hasta mañana ${fmt(fechaFin)} ${horaFin()}`
            : `Disponible ${duracion}hasta mañana ${fmt(fechaFin)} ${horaFin()}`;
        color = { bg: '#FFEBEE', text: '#B71C1C' };
    } else if (diasRestantes <= 3) {
        label = fmtInicio
            ? `Disponible ${duracion}del ${fmtInicio} al ${fmt(fechaFin)} ${horaFin()}`
            : `Disponible ${duracion}hasta ${fmt(fechaFin)} ${horaFin()}`;
        color = { bg: '#FFEBEE', text: '#B71C1C' };
    } else if (diasRestantes <= 7) {
        label = fmtInicio
            ? `Disponible ${duracion}del ${fmtInicio} al ${fmt(fechaFin)} ${horaFin()}`
            : `Disponible ${duracion}hasta ${fmt(fechaFin)} ${horaFin()}`;
        color = { bg: '#FFF8E1', text: '#E65100' };
    } else {
        label = fmtInicio
            ? `Disponible ${duracion}del ${fmtInicio} al ${fmt(fechaFin)} ${horaFin()}`
            : `Disponible ${duracion}hasta ${fmt(fechaFin)} ${horaFin()}`;
        color = { bg: '#E8F5E9', text: '#1B5E20' };
    }

    return { label, color };
};

const ModalMaterialesPendientes = ({ open, materiales, tituloCurso, onCerrar }) => {
    const [pagina, setPagina] = useState(1);

    useEffect(() => {
        if (open) setPagina(1);
    }, [open, materiales]);

    const totalPaginas = Math.ceil(materiales.length / ITEMS_POR_PAGINA);
    const inicio = (pagina - 1) * ITEMS_POR_PAGINA;
    const materialesPag = materiales.slice(inicio, inicio + ITEMS_POR_PAGINA);

    const handlePagina = (_, valor) => setPagina(valor);

    return (
        <ModalGeneral
            open={open}
            titulo={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Badge badgeContent={materiales.length} color="error" max={99}>
                        <NotificationsActive sx={{ color: 'text.secondary', fontSize: 22 }} />
                    </Badge>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                            Avisos del curso {tituloCurso || 'Curso'}
                        </Typography>
                    </Box>
                </Box>
            }
            width="sm"
            OnCerrar={onCerrar}
            acciones={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, width: '100%' }}>
                    {totalPaginas > 1 ? (
                        <Pagination
                            count={totalPaginas}
                            page={pagina}
                            onChange={handlePagina}
                            size="small"
                            color="primary"
                            siblingCount={0}
                        />
                    ) : (
                        <Box />
                    )}
                    <Button variant="outlined" color="inherit" size="small" onClick={onCerrar}>
                        Cerrar
                    </Button>
                </Box>
            }
        >
            <Box sx={{ px: 4 }}>
                <Divider sx={{ mb: 2 }} />

                {materiales.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <NotificationsActive sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                        <Typography variant="body2">Sin materiales pendientes con periodo activo.</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {materialesPag.map((mat) => {
                            const badge = getBadgeInfo(mat.fechaInicio, mat.fechaFin, mat.diasDuracion);
                            return (
                                <ListItem
                                    key={mat.idDetalleActividad}
                                    disableGutters
                                    alignItems="flex-start"
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        mb: 1.5,
                                        px: 2.5,
                                        py: 2.25,
                                        display: 'flex',
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 30, mt: '3px' }}>
                                        <DescriptionOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </ListItemIcon>

                                    <ListItemText
                                        disableTypography
                                        primary={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                    mb: 0.75
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: 'text.primary',
                                                        lineHeight: 1.4
                                                    }}
                                                >
                                                    {mat.titulo}
                                                </Typography>

                                                {badge && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            px: 1,
                                                            py: '2px',
                                                            borderRadius: 1,
                                                            backgroundColor: badge.color.bg,
                                                            color: badge.color.text,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {badge.label}
                                                    </Box>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                {mat.tituloSesion && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                                        Sesión: {mat.tituloSesion}
                                                    </Typography>
                                                )}
                                                {mat.tituloActividad && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                                        Actividad: {mat.tituloActividad}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Box>
        </ModalGeneral>
    );
};

export default ModalMaterialesPendientes;
